/**
 * useRegulatoryCompliance Hook
 *
 * Repository pattern hook for regulatory compliance configuration (HIPAA §164.514, FDA guidance)
 *
 * Features:
 * - Real-time threshold configuration (HIPAA, FDA, GDPR, SOX)
 * - Bias detection in AI-generated workflows (FDA requirement)
 * - Emergency override workflows with justification
 * - Complete audit trail for threshold changes
 * - Multi-jurisdiction compliance support
 *
 * Compliance Frameworks:
 * - HIPAA §164.514: De-identification thresholds
 * - FDA: AI bias detection in clinical workflows
 * - GDPR: Data minimization and consent thresholds
 * - SOX: Financial control thresholds
 *
 * @module useRegulatoryCompliance
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// TypeScript Interfaces (matching backend GraphQL types)
// ============================================================================

/**
 * Regulatory threshold configuration
 * Maps to RegulatoryThresholdGql backend type
 */
export interface RegulatoryThreshold {
  id: string;
  tenant: string;
  thresholdType: string;
  frameworkId: string; // "HIPAA", "FDA", "GDPR", "SOX"
  numericValue: number;
  unit: string;
  confidenceScore?: number; // Bias detection score (0-1)
  effectiveDate?: string;
  sourceAuthority?: string; // e.g., "HIPAA §164.514(b)"
  metadata?: Record<string, unknown>;
}

/**
 * Input for updating regulatory thresholds
 */
export interface UpdateRegulatoryThresholdInput {
  id: string;
  tenant: string;
  thresholdType: string;
  frameworkId: string;
  numericValue: number;
  unit: string;
  confidenceScore?: number;
  effectiveDate?: string;
  sourceAuthority?: string;
  justification: string; // Required for audit trail
  metadata?: Record<string, unknown>;
}

/**
 * Input for emergency override requests
 */
export interface EmergencyOverrideInput {
  id: string;
  tenant: string;
  thresholdType: string;
  frameworkId: string;
  overrideValue: number;
  unit: string;
  justification: string; // MANDATORY for emergency overrides
  expectedDuration: string; // "1hr", "6hr", "24hr", "48hr"
  approverId?: string;
  incidentId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Compliance framework definition (matches backend ComplianceFrameworkGql schema)
 */
export interface ComplianceFramework {
  frameworkId: string;
  name: string;
  description: string | null;
  category: string;
  regulatoryAuthority: string | null;
  applicableDomains: string[];
  severityLevel: number;
}

/**
 * Bias score metrics for AI workflows
 */
export interface BiasScoreMetrics {
  thresholdId: string;
  currentScore: number; // 0-1 (0 = no bias, 1 = high bias)
  trend: 'increasing' | 'decreasing' | 'stable';
  protectedClassImpact: Record<string, number>; // {"age": 0.3, "gender": 0.1}
  lastCalculated: string;
}

/**
 * Emergency override status
 */
export interface EmergencyOverride {
  id: string;
  thresholdId: string;
  overrideValue: number;
  justification: string;
  expectedDuration: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'revoked';
  approverId?: string;
  createdAt: string;
}

/**
 * Hook return type
 */
export interface UseRegulatoryComplianceReturn {
  // Data
  thresholds: RegulatoryThreshold[];
  frameworks: ComplianceFramework[];
  biasScores: BiasScoreMetrics[];
  activeOverrides: EmergencyOverride[];

  // Loading states
  loading: boolean;
  subscriptionActive: boolean;

  // Error handling
  error: Error | null;

  // Mutations
  updateThreshold: (input: UpdateRegulatoryThresholdInput) => Promise<boolean>;
  requestEmergencyOverride: (input: EmergencyOverrideInput) => Promise<boolean>;

  // Filtering
  filterByFramework: (frameworkId: string) => RegulatoryThreshold[];
  filterByThresholdType: (thresholdType: string) => RegulatoryThreshold[];

  // Bias detection
  calculateBiasScore: (thresholdId: string) => number | null;
  getHighBiasThresholds: (threshold?: number) => RegulatoryThreshold[];

  // Audit trail
  getThresholdHistory: (thresholdId: string) => Promise<RegulatoryThreshold[]>;
}

// ============================================================================
// GraphQL Queries and Mutations
// ============================================================================

/**
 * Query: Get regulatory thresholds batch (matches backend schema)
 */
const GET_REGULATORY_THRESHOLDS_BATCH = gql`
  query GetRegulatoryThresholdsBatch($tenant: String!, $inputs: [RegulatoryThresholdQueryInput!]!) {
    getRegulatoryThresholdsBatch(tenant: $tenant, inputs: $inputs) {
      tenant
      thresholdType
      frameworkId
      numericValue
      unit
      effectiveDate
      sourceAuthority
    }
  }
`;

/**
 * Query: Get compliance configuration
 */
const GET_COMPLIANCE_CONFIG = gql`
  query GetRegulatoryComplianceConfig($tenant: String!) {
    getRegulatoryComplianceConfig(tenant: $tenant) {
      tenant
      thresholdType
      frameworkId
      numericValue
      unit
      effectiveDate
      sourceAuthority
    }
  }
`;

/**
 * Query: Get available compliance frameworks (matches backend schema)
 */
const GET_AVAILABLE_COMPLIANCE_FRAMEWORKS = gql`
  query GetAvailableComplianceFrameworks($tenant: String!) {
    getAvailableComplianceFrameworks(tenant: $tenant) {
      frameworkId
      name
      description
      category
      regulatoryAuthority
      applicableDomains
      severityLevel
    }
  }
`;

/**
 * Mutation: Update regulatory threshold
 */
const UPDATE_REGULATORY_THRESHOLD = gql`
  mutation UpdateRegulatoryThreshold($input: UpdateRegulatoryThresholdInput!) {
    updateRegulatoryThreshold(input: $input)
  }
`;

/**
 * Mutation: Request emergency override
 */
const EMERGENCY_OVERRIDE_THRESHOLD = gql`
  mutation EmergencyOverrideRegulatoryThreshold($input: EmergencyOverrideInput!) {
    emergencyOverrideRegulatoryThreshold(input: $input)
  }
`;

/**
 * Subscription: Real-time threshold configuration changes
 */
const THRESHOLD_CHANGES_SUBSCRIPTION = gql`
  subscription OnThresholdChange($tenant: String!) {
    thresholdChanged(tenant: $tenant) {
      id
      tenant
      thresholdType
      frameworkId
      numericValue
      unit
      confidenceScore
      effectiveDate
      sourceAuthority
      metadata
    }
  }
`;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * useRegulatoryCompliance Hook
 *
 * Repository pattern hook for regulatory compliance configuration
 *
 * @param tenant - Tenant ID (from useCurrentTenant hook)
 * @param frameworkId - Optional framework filter ("HIPAA", "FDA", "GDPR", "SOX")
 * @returns UseRegulatoryComplianceReturn
 *
 * @example
 * ```tsx
 * const {
 *   thresholds,
 *   frameworks,
 *   biasScores,
 *   updateThreshold,
 *   requestEmergencyOverride,
 *   filterByFramework,
 *   calculateBiasScore,
 * } = useRegulatoryCompliance('tenant-123', 'HIPAA');
 * ```
 */
export function useRegulatoryCompliance(
  tenant: string,
  frameworkId?: string
): UseRegulatoryComplianceReturn {
  // ============================================================================
  // State Management
  // ============================================================================

  const [thresholds, setThresholds] = useState<RegulatoryThreshold[]>([]);
  const [frameworks, setFrameworks] = useState<ComplianceFramework[]>([]);
  const [biasScores, setBiasScores] = useState<BiasScoreMetrics[]>([]);
  const [activeOverrides, setActiveOverrides] = useState<EmergencyOverride[]>([]);
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  // ============================================================================
  // GraphQL Queries
  // ============================================================================

  // Build query inputs for batch request
  const thresholdInputs = [
    { thresholdType: 'OVERALL_SAFETY_SCORE', frameworkId: frameworkId || 'LIFE_CRITICAL' },
    { thresholdType: 'HEALTHCARE_SAFETY_SCORE', frameworkId: frameworkId || 'LIFE_CRITICAL' },
    { thresholdType: 'FINANCIAL_SAFETY_SCORE', frameworkId: frameworkId || 'LIFE_CRITICAL' },
    { thresholdType: 'LEGAL_SAFETY_SCORE', frameworkId: frameworkId || 'LIFE_CRITICAL' },
    { thresholdType: 'OPERATIONAL_SAFETY_SCORE', frameworkId: frameworkId || 'LIFE_CRITICAL' },
    { thresholdType: 'EMERGENCY_RESPONSE_THRESHOLD', frameworkId: frameworkId || 'LIFE_CRITICAL' },
  ];

  const {
    data: thresholdsData,
    loading: thresholdsLoading,
    error: thresholdsError,
    refetch: refetchThresholds,
  } = useQuery<Record<string, any>>(GET_REGULATORY_THRESHOLDS_BATCH, {
    variables: { tenant, inputs: thresholdInputs },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: frameworksData,
    loading: frameworksLoading,
    error: frameworksError,
  } = useQuery<Record<string, any>>(GET_AVAILABLE_COMPLIANCE_FRAMEWORKS, {
    variables: { tenant },
    fetchPolicy: 'cache-first',
  });

  // ============================================================================
  // GraphQL Mutations
  // ============================================================================

  const [updateThresholdMutation] = useMutation<Record<string, any>>(UPDATE_REGULATORY_THRESHOLD);
  const [emergencyOverrideMutation] = useMutation<Record<string, any>>(EMERGENCY_OVERRIDE_THRESHOLD);

  // ============================================================================
  // GraphQL Subscriptions
  // ============================================================================

  // TODO: Enable subscription when backend implements thresholdChanged subscription
  // const { data: subscriptionData } = useSubscription<Record<string, any>>(THRESHOLD_CHANGES_SUBSCRIPTION, {
  //   variables: { tenant },
  //   onData: () => {
  //     setSubscriptionActive(true);
  //   },
  //   onError: (error: any) => {
  //     console.error('[useRegulatoryCompliance] Subscription error:', error);
  //     setSubscriptionActive(false);
  //   },
  // });
  const subscriptionData = null as { thresholdChanged?: RegulatoryThreshold } | null; // Temporarily disabled until backend implements subscription

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Update thresholds from query data
   */
  useEffect(() => {
    if (thresholdsData?.getRegulatoryThresholdsBatch) {
      // Convert backend format to hook format
      const convertedThresholds = thresholdsData.getRegulatoryThresholdsBatch.map((t: any) => ({
        id: `${t.tenant}-${t.frameworkId}-${t.thresholdType}`, // Generate ID from composite key
        tenant: t.tenant,
        thresholdType: t.thresholdType,
        frameworkId: t.frameworkId,
        numericValue: t.numericValue,
        unit: t.unit,
        effectiveDate: t.effectiveDate,
        sourceAuthority: t.sourceAuthority,
      }));
      setThresholds(convertedThresholds);
    }
  }, [thresholdsData]);

  /**
   * Update frameworks from query data
   */
  useEffect(() => {
    if (frameworksData?.getAvailableComplianceFrameworks) {
      setFrameworks(frameworksData.getAvailableComplianceFrameworks);
    }
  }, [frameworksData]);

  /**
   * Handle real-time threshold changes from subscription
   */
  useEffect(() => {
    if (subscriptionData?.thresholdChanged) {
      const updatedThreshold = subscriptionData.thresholdChanged;

      setThresholds((prev) => {
        const index = prev.findIndex((t) => t.id === updatedThreshold.id);
        if (index !== -1) {
          // Update existing threshold
          const updated = [...prev];
          updated[index] = updatedThreshold;
          return updated;
        } else {
          // Add new threshold
          return [...prev, updatedThreshold];
        }
      });
    }
  }, [subscriptionData]);

  /**
   * Calculate bias scores for all thresholds with confidence scores
   */
  useEffect(() => {
    const scores: BiasScoreMetrics[] = thresholds
      .filter((t) => t.confidenceScore !== undefined)
      .map((t) => ({
        thresholdId: t.id,
        currentScore: t.confidenceScore!,
        trend: 'stable', // TODO: Calculate trend from historical data
        protectedClassImpact: {}, // TODO: Load from metadata
        lastCalculated: new Date().toISOString(),
      }));

    setBiasScores(scores);
  }, [thresholds]);

  // ============================================================================
  // Mutation Handlers
  // ============================================================================

  /**
   * Update regulatory threshold
   *
   * @param input - Update threshold input
   * @returns Promise<boolean> - Success status
   */
  const updateThreshold = useCallback(
    async (input: UpdateRegulatoryThresholdInput): Promise<boolean> => {
      try {
        const { data } = await updateThresholdMutation({
          variables: { input },
        });

        if (data?.updateRegulatoryThreshold) {
          // Refetch thresholds to get updated data
          await refetchThresholds();
          return true;
        }

        return false;
      } catch (error) {
        console.error('[useRegulatoryCompliance] Update threshold error:', error);
        throw error;
      }
    },
    [updateThresholdMutation, refetchThresholds]
  );

  /**
   * Request emergency override
   *
   * @param input - Emergency override input
   * @returns Promise<boolean> - Success status
   */
  const requestEmergencyOverride = useCallback(
    async (input: EmergencyOverrideInput): Promise<boolean> => {
      try {
        const { data } = await emergencyOverrideMutation({
          variables: { input },
        });

        if (data?.emergencyOverrideRegulatoryThreshold) {
          // Refetch thresholds to get updated data
          await refetchThresholds();

          // TODO: Backend should return the complete EmergencyOverride object with backend-generated ID
          // For now, just refetch to get updated state from backend
          // const override = data?.emergencyOverrideRegulatoryThreshold;
          // setActiveOverrides((prev) => [...prev, override]);

          return true;
        }

        return false;
      } catch (error) {
        console.error('[useRegulatoryCompliance] Emergency override error:', error);
        throw error;
      }
    },
    [emergencyOverrideMutation, refetchThresholds]
  );

  // ============================================================================
  // Filtering Functions
  // ============================================================================

  /**
   * Filter thresholds by compliance framework
   *
   * @param frameworkId - Framework ID ("HIPAA", "FDA", "GDPR", "SOX")
   * @returns Filtered thresholds
   */
  const filterByFramework = useCallback(
    (frameworkId: string): RegulatoryThreshold[] => {
      return thresholds.filter((t) => t.frameworkId === frameworkId);
    },
    [thresholds]
  );

  /**
   * Filter thresholds by threshold type
   *
   * @param thresholdType - Threshold type (e.g., "de_identification_threshold")
   * @returns Filtered thresholds
   */
  const filterByThresholdType = useCallback(
    (thresholdType: string): RegulatoryThreshold[] => {
      return thresholds.filter((t) => t.thresholdType === thresholdType);
    },
    [thresholds]
  );

  // ============================================================================
  // Bias Detection Functions
  // ============================================================================

  /**
   * Calculate bias score for specific threshold
   *
   * @param thresholdId - Threshold ID
   * @returns Bias score (0-1) or null if not found
   */
  const calculateBiasScore = useCallback(
    (thresholdId: string): number | null => {
      const metrics = biasScores.find((m) => m.thresholdId === thresholdId);
      return metrics?.currentScore ?? null;
    },
    [biasScores]
  );

  /**
   * Get thresholds with high bias scores
   *
   * @param threshold - Bias threshold (default 0.7 per FDA guidance)
   * @returns Thresholds with bias score >= threshold
   */
  const getHighBiasThresholds = useCallback(
    (threshold: number = 0.7): RegulatoryThreshold[] => {
      const highBiasIds = biasScores
        .filter((m) => m.currentScore >= threshold)
        .map((m) => m.thresholdId);

      return thresholds.filter((t) => highBiasIds.includes(t.id));
    },
    [thresholds, biasScores]
  );

  // ============================================================================
  // Audit Trail Functions
  // ============================================================================

  /**
   * Get threshold change history
   *
   * @param thresholdId - Threshold ID
   * @returns Promise<RegulatoryThreshold[]> - Historical thresholds
   */
  const getThresholdHistory = useCallback(
    async (thresholdId: string): Promise<RegulatoryThreshold[]> => {
      // TODO: Implement audit trail query
      console.warn('[useRegulatoryCompliance] getThresholdHistory not yet implemented');
      return [];
    },
    []
  );

  // ============================================================================
  // Computed Values
  // ============================================================================

  const loading = thresholdsLoading || frameworksLoading;
  const error = thresholdsError || frameworksError || null;

  // ============================================================================
  // Return Hook Interface
  // ============================================================================

  return {
    // Data
    thresholds,
    frameworks,
    biasScores,
    activeOverrides,

    // Loading states
    loading,
    subscriptionActive,

    // Error handling
    error,

    // Mutations
    updateThreshold,
    requestEmergencyOverride,

    // Filtering
    filterByFramework,
    filterByThresholdType,

    // Bias detection
    calculateBiasScore,
    getHighBiasThresholds,

    // Audit trail
    getThresholdHistory,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate expiration time from expected duration
 *
 * @param duration - Duration string ("1hr", "6hr", "24hr", "48hr")
 * @returns ISO timestamp
 */
function calculateExpirationTime(duration: string): string {
  const now = new Date();
  const hours = parseInt(duration.replace('hr', ''), 10);

  if (isNaN(hours)) {
    console.error(`[calculateExpirationTime] Invalid duration: ${duration}`);
    return now.toISOString();
  }

  now.setHours(now.getHours() + hours);
  return now.toISOString();
}
